import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Checkbox,
  FormControlLabel,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  LinearProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Home as HomeIcon,
  GetApp as DownloadIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

interface ChecklistItem {
  id: string;
  task: string;
  description: string;
  timeframe: string;
  responsible: 'Buyer' | 'Seller' | 'Agent' | 'Title Company' | 'Lender';
  category: 'Pre-Contract' | 'Under Contract' | 'Pre-Closing' | 'Closing Day';
  isRequired: boolean;
  njSpecific?: boolean;
  tips?: string;
}

const checklistItems: ChecklistItem[] = [
  // Pre-Contract
  {
    id: 'pc1',
    task: 'Get Pre-Approved for Mortgage',
    description: 'Obtain pre-approval letter from a qualified lender.',
    timeframe: 'Before house hunting',
    responsible: 'Buyer',
    category: 'Pre-Contract',
    isRequired: true,
    tips: 'Shop around with multiple lenders for best rates.',
  },
  {
    id: 'pc2',
    task: 'Find Real Estate Agent',
    description: 'Choose an agent familiar with Middlesex County markets.',
    timeframe: 'Before house hunting',
    responsible: 'Buyer',
    category: 'Pre-Contract',
    isRequired: false,
    tips: 'Look for agents with experience in your target townships.',
  },
  {
    id: 'pc3',
    task: 'Research Neighborhoods',
    description: 'Investigate schools, taxes, and local amenities in target areas.',
    timeframe: 'Before making offers',
    responsible: 'Buyer',
    category: 'Pre-Contract',
    isRequired: false,
  },

  // Under Contract
  {
    id: 'uc1',
    task: 'Order Home Inspection',
    description: 'Schedule professional inspection within contract timeframe.',
    timeframe: '5-10 days after contract',
    responsible: 'Buyer',
    category: 'Under Contract',
    isRequired: true,
    tips: 'NJ law allows 10 days for inspection unless otherwise specified.',
  },
  {
    id: 'uc2',
    task: 'Apply for Mortgage',
    description: 'Submit formal mortgage application with chosen lender.',
    timeframe: 'Within 3 days of contract',
    responsible: 'Buyer',
    category: 'Under Contract',
    isRequired: true,
  },
  {
    id: 'uc3',
    task: 'Order Appraisal',
    description: 'Lender orders appraisal to confirm property value.',
    timeframe: '1-2 weeks after application',
    responsible: 'Lender',
    category: 'Under Contract',
    isRequired: true,
  },
  {
    id: 'uc4',
    task: 'Review Property Disclosure',
    description: 'Carefully review seller\'s property condition disclosure.',
    timeframe: 'Within 5 days of receipt',
    responsible: 'Buyer',
    category: 'Under Contract',
    isRequired: true,
    njSpecific: true,
    tips: 'NJ requires sellers to provide detailed property condition disclosure.',
  },
  {
    id: 'uc5',
    task: 'Order Title Search',
    description: 'Title company begins comprehensive title examination.',
    timeframe: 'Within 1 week of contract',
    responsible: 'Title Company',
    category: 'Under Contract',
    isRequired: true,
  },
  {
    id: 'uc6',
    task: 'Review HOA Documents',
    description: 'If applicable, review homeowners association bylaws and financials.',
    timeframe: 'Within 5 days of receipt',
    responsible: 'Buyer',
    category: 'Under Contract',
    isRequired: false,
  },

  // Pre-Closing
  {
    id: 'pc1',
    task: 'Obtain Homeowners Insurance',
    description: 'Secure adequate coverage and provide proof to lender.',
    timeframe: '2 weeks before closing',
    responsible: 'Buyer',
    category: 'Pre-Closing',
    isRequired: true,
    tips: 'Shop multiple carriers for best rates and coverage.',
  },
  {
    id: 'pc2',
    task: 'Final Mortgage Approval',
    description: 'Receive clear to close from lender.',
    timeframe: '3-5 days before closing',
    responsible: 'Lender',
    category: 'Pre-Closing',
    isRequired: true,
  },
  {
    id: 'pc3',
    task: 'Review Closing Disclosure',
    description: 'Carefully review all closing costs and loan terms.',
    timeframe: '3 days before closing',
    responsible: 'Buyer',
    category: 'Pre-Closing',
    isRequired: true,
    tips: 'Compare to initial loan estimate for any unexpected changes.',
  },
  {
    id: 'pc4',
    task: 'Arrange Closing Funds',
    description: 'Obtain certified funds for down payment and closing costs.',
    timeframe: '1-2 days before closing',
    responsible: 'Buyer',
    category: 'Pre-Closing',
    isRequired: true,
    tips: 'Wire transfers are preferred; personal checks not accepted.',
  },
  {
    id: 'pc5',
    task: 'Final Walk-Through',
    description: 'Inspect property condition and verify agreed repairs completed.',
    timeframe: '24 hours before closing',
    responsible: 'Buyer',
    category: 'Pre-Closing',
    isRequired: true,
  },
  {
    id: 'pc6',
    task: 'Coordinate Utilities Transfer',
    description: 'Arrange for utility accounts to be transferred to your name.',
    timeframe: '1 week before closing',
    responsible: 'Buyer',
    category: 'Pre-Closing',
    isRequired: false,
    njSpecific: true,
    tips: 'Contact PSE&G, NJ American Water, and local providers.',
  },

  // Closing Day
  {
    id: 'cd1',
    task: 'Bring Valid Photo ID',
    description: 'Government-issued photo identification required.',
    timeframe: 'Day of closing',
    responsible: 'Buyer',
    category: 'Closing Day',
    isRequired: true,
  },
  {
    id: 'cd2',
    task: 'Bring Certified Funds',
    description: 'Exact amount as specified in closing disclosure.',
    timeframe: 'Day of closing',
    responsible: 'Buyer',
    category: 'Closing Day',
    isRequired: true,
  },
  {
    id: 'cd3',
    task: 'Review All Documents',
    description: 'Carefully read and understand each document before signing.',
    timeframe: 'During closing',
    responsible: 'Buyer',
    category: 'Closing Day',
    isRequired: true,
    tips: 'Don\'t hesitate to ask questions about any document.',
  },
  {
    id: 'cd4',
    task: 'Receive Keys and Garage Remotes',
    description: 'Obtain all keys, codes, and access devices.',
    timeframe: 'After closing completion',
    responsible: 'Seller',
    category: 'Closing Day',
    isRequired: true,
  },
  {
    id: 'cd5',
    task: 'Receive Deed and Title Policy',
    description: 'Obtain recorded deed and owner\'s title insurance policy.',
    timeframe: 'After closing completion',
    responsible: 'Title Company',
    category: 'Closing Day',
    isRequired: true,
  },
];

const categories = ['All', 'Pre-Contract', 'Under Contract', 'Pre-Closing', 'Closing Day'];

const MiddlesexCountyClosingChecklist: React.FC = () => {
  const [completedItems, setCompletedItems] = useState<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState('All');

  const handleItemToggle = (itemId: string) => {
    const newCompleted = new Set(completedItems);
    if (newCompleted.has(itemId)) {
      newCompleted.delete(itemId);
    } else {
      newCompleted.add(itemId);
    }
    setCompletedItems(newCompleted);
  };

  const filteredItems = checklistItems.filter(item => 
    selectedCategory === 'All' || item.category === selectedCategory
  );

  const totalItems = checklistItems.length;
  const completedCount = completedItems.size;
  const progressPercentage = (completedCount / totalItems) * 100;

  const getResponsibleColor = (responsible: string) => {
    switch (responsible) {
      case 'Buyer': return '#1e3a8a';
      case 'Seller': return '#059669';
      case 'Agent': return '#f59e0b';
      case 'Title Company': return '#dc2626';
      case 'Lender': return '#7c3aed';
      default: return '#6b7280';
    }
  };

  return (
    <>
      <Helmet>
        <title>Middlesex County Closing Checklist | MAD Title Agency - Homebuyer Guide</title>
        <meta
          name="description"
          content="Complete closing checklist for Middlesex County homebuyers. Essential tasks, timelines, and NJ-specific requirements for Woodbridge, Edison, and surrounding areas."
        />
        <meta
          name="keywords"
          content="Middlesex County closing checklist, NJ homebuyer checklist, Woodbridge closing guide, Edison home buying, New Jersey real estate process"
        />
      </Helmet>

      {/* Breadcrumb */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link> →{' '}
            <Link to="/resources" style={{ textDecoration: 'none', color: 'inherit' }}>Resources</Link> →{' '}
            Middlesex County Closing Checklist
          </Typography>
        </Container>
      </Box>

      {/* Header */}
      <Box sx={{ backgroundColor: '#1e3a8a', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label="Closing Checklist"
                sx={{
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  mb: 2,
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                }}
              >
                Middlesex County Closing Checklist
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
                Your complete guide to a successful closing in Woodbridge, Edison, and surrounding areas
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  sx={{
                    backgroundColor: '#f59e0b',
                    '&:hover': { backgroundColor: '#d97706' },
                  }}
                >
                  Download Checklist
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': { borderColor: '#f59e0b', backgroundColor: 'rgba(245, 158, 11, 0.1)' },
                  }}
                >
                  Print Checklist
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
                <Box sx={{ textAlign: 'center', color: '#1e3a8a' }}>
                  <ScheduleIcon sx={{ fontSize: 48, mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Progress Tracker
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    {completedCount}/{totalItems}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={progressPercentage}
                    sx={{ mb: 1, height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {Math.round(progressPercentage)}% Complete
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Introduction */}
        <Box sx={{ mb: 6 }}>
          <Alert severity="info" sx={{ mb: 4 }}>
            <Typography variant="body2">
              This checklist is specifically designed for Middlesex County transactions and includes 
              New Jersey-specific requirements and timelines. Use the interactive checkboxes to track 
              your progress toward closing.
            </Typography>
          </Alert>

          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 600, color: '#1e3a8a' }}>
                Your Path to Closing Success
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                Closing on a home in Middlesex County involves multiple steps, deadlines, and parties. 
                This comprehensive checklist ensures you don't miss any critical tasks and helps you 
                stay organized throughout the process.
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                Tasks are organized by timeline and clearly indicate who is responsible for each item. 
                Special attention is given to New Jersey-specific requirements that may differ from 
                other states.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Quick Tips:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Start early"
                      secondary="Begin tasks as soon as possible"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Stay organized"
                      secondary="Keep all documents in one place"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Ask questions"
                      secondary="Don't hesitate to contact your team"
                    />
                  </ListItem>
                </List>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Category Filter */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Filter by Phase:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {categories.map((category) => (
              <Chip
                key={category}
                label={category}
                variant={selectedCategory === category ? 'filled' : 'outlined'}
                color={selectedCategory === category ? 'primary' : 'default'}
                onClick={() => setSelectedCategory(category)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Box>

        {/* Checklist Items */}
        <Box sx={{ mb: 8 }}>
          {categories.slice(1).map((category) => {
            const categoryItems = checklistItems.filter(item => item.category === category);
            const categoryCompleted = categoryItems.filter(item => completedItems.has(item.id)).length;
            
            if (selectedCategory !== 'All' && selectedCategory !== category) {
              return null;
            }

            return (
              <Card key={category} sx={{ mb: 4, border: '1px solid #e2e8f0' }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ p: 3, backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e3a8a' }}>
                        {category}
                      </Typography>
                      <Chip
                        label={`${categoryCompleted}/${categoryItems.length} Complete`}
                        color={categoryCompleted === categoryItems.length ? 'success' : 'default'}
                        variant={categoryCompleted === categoryItems.length ? 'filled' : 'outlined'}
                      />
                    </Box>
                  </Box>
                  
                  {categoryItems.map((item, index) => (
                    <Box key={item.id} sx={{ p: 3, borderBottom: index < categoryItems.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                      <Grid container spacing={3} alignItems="flex-start">
                        <Grid item xs={12} sm={1}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={completedItems.has(item.id)}
                                onChange={() => handleItemToggle(item.id)}
                                color="primary"
                              />
                            }
                            label=""
                            sx={{ m: 0 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={11}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 600,
                                  textDecoration: completedItems.has(item.id) ? 'line-through' : 'none',
                                  color: completedItems.has(item.id) ? 'text.secondary' : 'text.primary',
                                }}
                              >
                                {item.task}
                                {item.isRequired && (
                                  <Chip
                                    label="Required"
                                    size="small"
                                    color="error"
                                    sx={{ ml: 1, height: 20 }}
                                  />
                                )}
                                {item.njSpecific && (
                                  <Chip
                                    label="NJ Specific"
                                    size="small"
                                    sx={{
                                      ml: 1,
                                      height: 20,
                                      backgroundColor: '#f59e0b',
                                      color: 'white',
                                    }}
                                  />
                                )}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                              <Chip
                                label={item.responsible}
                                size="small"
                                sx={{
                                  backgroundColor: getResponsibleColor(item.responsible),
                                  color: 'white',
                                }}
                              />
                              <Chip
                                label={item.timeframe}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                          
                          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                            {item.description}
                          </Typography>
                          
                          {item.tips && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                              <Typography variant="body2">
                                <strong>Tip:</strong> {item.tips}
                              </Typography>
                            </Alert>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </Box>

        {/* Local Resources */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Middlesex County Resources
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  County Clerk Offices
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  For recording deeds and other documents:
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Middlesex County Clerk</strong><br />
                  75 Bayard Street<br />
                  New Brunswick, NJ 08901<br />
                  (732) 745-3005
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Tax Information
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  For property tax certificates and information:
                </Typography>
                <Typography variant="body2">
                  Contact your specific township tax collector:<br />
                  • Woodbridge: (732) 602-6000<br />
                  • Edison: (732) 248-7298<br />
                  • Perth Amboy: (732) 826-0290<br />
                  • New Brunswick: (732) 745-5175
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600, color: '#64748b' }}>
              Ready to Start Your Closing Process?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
              Our experienced team guides you through every step of your Middlesex County closing.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Start Your Closing
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/resources/closing-cost-calculator"
                sx={{
                  borderColor: '#1e3a8a',
                  color: '#1e3a8a',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Calculate Closing Costs
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default MiddlesexCountyClosingChecklist; 